import React from "react";
import { Bar, Line } from "react-chartjs-2";

export default function SeniorView({ data, loading }) {
  const rows = data?.rows || [];
  const summary = data?.summary || {};

  // ---- TOTALI (da summary o calcolati sui rows) ----
  const totAppRil =
    summary.tot_app_rilevatore ??
    rows.reduce((acc, r) => acc + Number(r.app_rilevatore || 0), 0);

  const totAppSenior =
    summary.tot_app_senior ??
    rows.reduce((acc, r) => acc + Number(r.app_senior || 0), 0);

  const totChiusi =
    summary.tot_chiusi ??
    rows.reduce((acc, r) => acc + Number(r.chiusi || 0), 0);

  const totFinanziati =
    summary.tot_chiusi_finanziati ??
    rows.reduce((acc, r) => acc + Number(r.chiusi_finanziati || 0), 0);

  const totNonChiusi =
    summary.tot_non_chiusi ??
    rows.reduce((acc, r) => acc + Number(r.non_chiusi || 0), 0);

  const totInAttesa =
    summary.tot_in_attesa ??
    rows.reduce((acc, r) => acc + Number(r.in_attesa || 0), 0);

  const totChiusiAnnullati =
    summary.tot_chiusi_annullati ??
    rows.reduce((acc, r) => acc + Number(r.chiusi_annullati || 0), 0);

  const fatturatoTot =
    summary.fatturato_eur ??
    rows.reduce((acc, r) => acc + Number(r.fatturato_eur || 0), 0);

  const kmTot =
    summary.km_tot ??
    rows.reduce((acc, r) => acc + Number(r.km || 0), 0);

  // ---- HELPER FORMATI / PERCENTUALI ----
  const safePerc = (num, den) => {
    if (!den || !Number.isFinite(num) || !Number.isFinite(den)) return 0;
    return num / den;
  };

  const fmtPerc = (val) =>
    Number(val || 0).toLocaleString("it-IT", {
      style: "percent",
      maximumFractionDigits: 1,
    });

  const fmtCurr = (val) =>
    Number(val || 0).toLocaleString("it-IT", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 0,
    });

  // ---- KPI DERIVATI TOTALI ----
  const convTot = safePerc(totChiusi, totAppSenior); // chiusi / app_senior
  const financeRateTot = safePerc(totFinanziati, totChiusi); // finanziati / chiusi
  const waitRateTot = safePerc(totInAttesa, totAppSenior);
  const annulRateTot = safePerc(totChiusiAnnullati, totChiusi);
  const ticketMedioTot = safePerc(fatturatoTot, totChiusi);
  const kmPerChiusuraTot = safePerc(kmTot, totChiusi);

  // ---- CHART BAR (chiusi per senior) ----
  const labels = rows.map((r) => r.nome || r.label || "—");
  const chiusiData = rows.map((r) => Number(r.chiusi || 0));

  const barData = {
    labels,
    datasets: [
      {
        label: "Appuntamenti chiusi",
        data: chiusiData,
        backgroundColor: labels.map(
          (_, i) => `hsl(${(i * 47) % 360} 70% 60%)`
        ),
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: "y",
    plugins: {
      legend: { display: false },
      title: {
        display: true,
        text: "Chiusure per senior",
        font: { size: 14, weight: 700 },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: "rgba(148,163,184,.25)" } },
    },
  };

  // ---- CHART LINE (fatturato timeline) ----
  const timeline = data?.timeline || [];
  const lineLabels = timeline.map((p) => p.date);

  // Fatturato del giorno
  const dailyValues = timeline.map((p) => Number(p.fatturato_eur || 0));

  // Fatturato cumulato sul periodo
  let running = 0;
  const cumulativeValues = timeline.map((p) => {
    running += Number(p.fatturato_eur || 0);
    return running;
  });

  const lineData = {
    labels: lineLabels,
    datasets: [
      {
        label: "Fatturato giornaliero",
        data: dailyValues,
        borderColor: "hsl(140 80% 60%)",
        tension: 0.25,
        fill: false,
        pointRadius: 2,
      },
      {
        label: "Fatturato cumulato",
        data: cumulativeValues,
        borderColor: "hsl(210 80% 60%)",
        borderDash: [6, 4],
        tension: 0.25,
        fill: false,
        pointRadius: 2,
      },
    ],
  };


  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: { display: true }, // clic sulla legenda per mostrare/nascondere le serie
      title: {
        display: true,
        text: "Fatturato giornaliero e cumulato (tutti i senior)",
        font: { size: 14, weight: 700 },
      },
    },
    scales: {
      x: { grid: { display: false } },
      y: { grid: { color: "rgba(148,163,184,.25)" } },
    },
  };



  return (
    <div className="stats-section">
      {/* ================= KPI TOP ================= */}
      <div className="stats-kpis">
        <div className="kpi-card">
          <div className="kpi-label">Appuntamenti da rilevatore</div>
          <div className="kpi-value">
            {Number(totAppRil).toLocaleString("it-IT")}
          </div>
          <div className="kpi-hint">Assegnati ai senior come esito da rilevatore</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Appuntamenti da senior</div>
          <div className="kpi-value">
            {Number(totAppSenior).toLocaleString("it-IT")}
          </div>
          <div className="kpi-hint">Tutti gli appuntamenti gestiti dai senior</div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Chiusure</div>
          <div className="kpi-value">
            {Number(totChiusi).toLocaleString("it-IT")}
          </div>
          <div className="kpi-hint">
            Conv. su app. senior: <strong>{fmtPerc(convTot)}</strong>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Chiusure finanziate</div>
          <div className="kpi-value">
            {Number(totFinanziati).toLocaleString("it-IT")}
          </div>
          <div className="kpi-hint">
            Su chiusi: <strong>{fmtPerc(financeRateTot)}</strong>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Non chiusi</div>
          <div className="kpi-value">
            {Number(totNonChiusi).toLocaleString("it-IT")}
          </div>
          <div className="kpi-hint">
            In attesa: {Number(totInAttesa).toLocaleString("it-IT")} (
            {fmtPerc(waitRateTot)})
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Chiusure annullate</div>
          <div className="kpi-value">
            {Number(totChiusiAnnullati).toLocaleString("it-IT")}
          </div>
          <div className="kpi-hint">
            Su chiusi: <strong>{fmtPerc(annulRateTot)}</strong>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Fatturato senior</div>
          <div className="kpi-value">{fmtCurr(fatturatoTot)}</div>
          <div className="kpi-hint">
            Ticket medio: <strong>{fmtCurr(ticketMedioTot)}</strong>
          </div>
        </div>

        <div className="kpi-card">
          <div className="kpi-label">Km totali</div>
          <div className="kpi-value">
            {Number(kmTot).toLocaleString("it-IT")}
          </div>
          <div className="kpi-hint">
            Km per chiusura:{" "}
            <strong>
              {Number(kmPerChiusuraTot || 0).toLocaleString("it-IT", {
                maximumFractionDigits: 1,
              })}{" "}
              km
            </strong>
          </div>
        </div>
      </div>

      {/* ================= CHARTS ================= */}
      <div className="charts-grid">
        <div className="chart-card">
          {loading ? (
            <div className="chart-empty">Caricamento…</div>
          ) : rows.length ? (
            <Bar data={barData} options={barOptions} />
          ) : (
            <div className="chart-empty">Nessun dato per i senior.</div>
          )}
        </div>

        <div className="chart-card">
          {loading ? (
            <div className="chart-empty">Caricamento…</div>
          ) : timeline.length ? (
            <Line data={lineData} options={lineOptions} />
          ) : (
            <div className="chart-empty">
              Nessuna timeline fatturato (aggiungila nel backend).
            </div>
          )}
        </div>
      </div>

      {/* ================= TABELLA DETTAGLIO ================= */}
      <div className="series-table-card">
        <div className="series-table-header">
          <div>
            <strong>Dettaglio senior</strong>
            <div className="series-table-subtitle">
              Appuntamenti da rilevatore, da senior, chiusi, finanziati,
              annullati, fatturato e km con relative %.
            </div>
          </div>
        </div>
        <div className="series-table-scroller">
          <table className="series-table">
            <thead>
              <tr>
                <th>Senior</th>
                <th>App. da rilevatore</th>
                <th>App. da senior</th>
                <th>Chiusi</th>
                <th>Conv. % (su senior)</th>
                <th>Chiusi finanziati</th>
                <th>% fin. su chiusi</th>
                <th>Non chiusi</th>
                <th>In attesa</th>
                <th>Chiusi annullati</th>
                <th>Fatturato</th>
                <th>Ticket medio</th>
                <th>Km</th>
                <th>Km / chiusura</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const appRil = Number(r.app_rilevatore || 0);
                const appSen = Number(r.app_senior || 0);
                const chiusi = Number(r.chiusi || 0);
                const chiusiFin = Number(r.chiusi_finanziati || 0);
                const nonChiusi = Number(r.non_chiusi || 0);
                const inAttesa = Number(r.in_attesa || 0);
                const chiusiAnn = Number(r.chiusi_annullati || 0);
                const fatt = Number(r.fatturato_eur || 0);
                const km = Number(r.km || 0);

                const conv = safePerc(chiusi, appSen);
                const finRate = safePerc(chiusiFin, chiusi);
                const ticketMedio = safePerc(fatt, chiusi);
                const kmPerChiusura = safePerc(km, chiusi);

                return (
                  <tr key={r.id || r.nome}>
                    <td>{r.nome || r.label || "—"}</td>
                    <td>{appRil}</td>
                    <td>{appSen}</td>
                    <td>{chiusi}</td>
                    <td>{fmtPerc(conv)}</td>
                    <td>{chiusiFin}</td>
                    <td>{fmtPerc(finRate)}</td>
                    <td>{nonChiusi}</td>
                    <td>{inAttesa}</td>
                    <td>{chiusiAnn}</td>
                    <td>{fmtCurr(fatt)}</td>
                    <td>{fmtCurr(ticketMedio)}</td>
                    <td>{km}</td>
                    <td>
                      {Number(kmPerChiusura || 0).toLocaleString("it-IT", {
                        maximumFractionDigits: 1,
                      })}{" "}
                      km
                    </td>
                  </tr>
                );
              })}
              {!rows.length && (
                <tr>
                  <td colSpan={14}>Nessun senior trovato.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
